import { createSelector } from 'reselect';
import { array, option, record, string } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { createAction, createReducer } from '@mews/framework';
import { keyBy } from '@mews/fp';
import { Bill, BillState } from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { ApplicationState, CommanderThunkAction } from '@mews-commander/types';
import { getContainer } from '@mews-commander/legacy-app-instance';
import prefix from '../prefix';
import moduleSelector from '../selector';
import {
    createBaseEntitySelectors,
    createEntityPropertyIncludesSelector,
    createEntitySelectorByCompanyId,
    createEntitySelectorByOwnerId,
    createEntitySelectorByAssociatedAccountId,
    Entities,
} from './utils';
import { getOrderItemsByBillId } from './orderItems';
import { getPaymentsByBillId } from './payments';

export const NAME = 'bills';

const NEW_BILLS_LOADED = prefix('BILLS_LOADED');
const NEW_BILLS_DELETED = prefix('BILLS_DELETED');
const OWNER_BILLS_DELETED = prefix('OWNER_BILLS_DELETED');

export const setBills = (bills: Bill[]) => createAction(NEW_BILLS_LOADED, { entities: bills });

export const deleteBills = (billIds: string[]) => createAction(NEW_BILLS_DELETED, billIds);

export const deleteBillsByOwnerIds = (ownerIds: string[]) => createAction(OWNER_BILLS_DELETED, ownerIds);

export const deleteAllAccountBills =
    (ownerId: string): CommanderThunkAction =>
    (dispatch, getState) => {
        const billIds = getBillIdsByOwnerId(getState(), { ownerId });
        return dispatch(deleteBills(billIds));
    };

export const [getBills, getBillsByIds, getBillByOwnerId, getBill] = createBaseEntitySelectors<
    ApplicationState,
    Bill,
    typeof NAME
>(moduleSelector, NAME);

export const billsReducer = createReducer(
    {},
    {
        [NEW_BILLS_LOADED]: (state: Entities<Bill>, { entities }: { entities: Bill[] }) =>
            entities && entities.length > 0
                ? {
                      ...state,
                      ...keyBy('id', entities),
                  }
                : state,
        [NEW_BILLS_DELETED]: (state: Entities<Bill>, ids: string[]) =>
            ids && ids.length > 0
                ? pipe(
                      state,
                      record.filterWithIndex((id, _) => !ids.includes(id))
                  )
                : state,
        [OWNER_BILLS_DELETED]: (state: Entities<Bill>, ownerIds: string[]) =>
            ownerIds && ownerIds.length > 0
                ? pipe(
                      state,
                      record.filter((b) => !ownerIds.includes(b.ownerId))
                  )
                : state,
    }
);

export const getBillsByOwnerId = createEntitySelectorByOwnerId(getBills);
export const getBillsByAssociatedAccountId = createEntitySelectorByAssociatedAccountId(getBills);
export const getBillsByCompanyId = createEntitySelectorByCompanyId(getBills);
export const getBillIdsByOwnerId = createSelector(
    getBillsByOwnerId,
    array.map((bill) => bill.id)
);

const isOpen = ({ state }: Bill) => state === BillState.Open;

export const getOpenBillIdsByOwnerId = createSelector(
    getBillsByOwnerId,
    flow(
        array.filter(isOpen),
        array.map((bill) => bill.id)
    )
);

export const getOpenBillIdsByAssociatedAccountId = createSelector(
    getBillsByAssociatedAccountId,
    flow(
        array.filter(isOpen),
        array.map((bill) => bill.id)
    )
);

const getBillsByAssociatedAccountIds = createEntityPropertyIncludesSelector(
    getBills,
    'associatedAccountId',
    'associatedAccountIds'
);

export const getOpenBillIdsByAssociatedAccountIds = createSelector(
    getBillsByAssociatedAccountIds,
    flow(
        array.filter(isOpen),
        array.map((bill) => bill.id)
    )
);

const getBillsByOwnerIds = createEntityPropertyIncludesSelector(getBills, 'ownerId', 'ownerIds');
export const getOpenBillsByOwnerIds = createSelector(getBillsByOwnerIds, array.filter(isOpen));

export const getBillIdsByOwnerIds = createSelector(
    getBillsByOwnerIds,
    array.map((bill) => bill.id)
);
export const getOpenBillIdsByOwnerIds = createSelector(
    getOpenBillsByOwnerIds,
    array.map((bill) => bill.id)
);

export const getBillsOwnerIds = flow(
    getBillsByIds,
    array.map((bill) => bill.ownerId),
    array.uniq(string.Eq)
);

export const filterRealBillIds = flow(
    getBillsByIds,
    array.filter(({ isVirtual }) => isVirtual === false),
    array.map((bill) => bill.id)
);

export const getBillName = createSelector(getBill, getContainer, (bill, container) => {
    const {
        appLocalizationComponent: { localizeText },
    } = container();
    const name = bill?.name;
    const isVirtual = bill?.isVirtual;

    if (isVirtual) {
        return `${localizeText(TextKey.UnpaidBillItems)} / ${localizeText(TextKey.DepositPlural)}`;
    }

    return name;
});

export const getBillOwnerId = flow(getBill, (bill) => bill?.ownerId);
export const isBillVirtual = flow(getBill, (bill) => bill?.isVirtual);

export const getAssociatedAccountIdsByBillsIds = createSelector(
    getBillsByIds,
    flow(
        array.map((bill) => option.fromNullable(bill.associatedAccountId)),
        array.compact,
        array.uniq(string.Eq)
    )
);

export const getOwnerIdsByBillsIds = createSelector(
    getBillsByIds,
    array.map((bill) => bill.ownerId)
);

export const getBillItemCount = createSelector(
    getOrderItemsByBillId,
    getPaymentsByBillId,
    (orderItems, payments) => orderItems.length + payments.length
);

export const hasCompanyClosedBill = flow(
    getBillsByCompanyId,
    array.some((bill) => bill.state === BillState.Closed)
);

export const getUniqueBillOwnerIdsByBillIds = flow(
    getBillsByIds,
    array.map(({ ownerId }) => ownerId),
    array.uniq(string.Eq)
);
